import { createAppRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';
import type {
  CommentAdminContextResult,
  CommentAdminListResult,
  CommentAdminQuery,
  CreateSensitiveWordPayload,
  CommentReportListResult,
  CommentReportQuery,
  ResolveCommentReportPayload,
  SensitiveWordListResult,
  SensitiveWordQuery,
  UpdateSensitiveWordPayload,
  UpdateSensitiveWordStatusPayload,
} from './types/comment';

const request = createAppRequest();
const COMMENT_PREFIX = '/open/comments';
const COMMENT_ADMIN_PREFIX = '/bff/comments';
const COMMENT_REPORT_PREFIX = '/bff/comment-reports';
const SENSITIVE_WORD_PREFIX = '/bff/sensitive-words';

export async function getAdminComments(
  params: CommentAdminQuery,
  options?: ApiRequestOptions,
) {
  return request<CommentAdminListResult>(COMMENT_ADMIN_PREFIX, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function getAdminCommentContext(
  commentUuid: string,
  options?: ApiRequestOptions,
) {
  return request<CommentAdminContextResult>(
    `${COMMENT_ADMIN_PREFIX}/${commentUuid}/context`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

export async function deleteAdminComment(
  commentUuid: string,
  options?: ApiRequestOptions,
) {
  return request<{ success?: boolean }>(`${COMMENT_ADMIN_PREFIX}/${commentUuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function getCommentReports(
  params: CommentReportQuery,
  options?: ApiRequestOptions,
) {
  return request<CommentReportListResult>(COMMENT_REPORT_PREFIX, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function resolveCommentReport(
  reportUuid: string,
  body: ResolveCommentReportPayload,
  options?: ApiRequestOptions,
) {
  return request<{ success?: boolean }>(
    `${COMMENT_REPORT_PREFIX}/${reportUuid}/resolve`,
    {
      method: 'POST',
      data: body,
      ...(options || {}),
    },
  );
}

export async function deleteComment(
  commentUuid: string,
  options?: ApiRequestOptions,
) {
  return request<{ success?: boolean }>(`${COMMENT_PREFIX}/${commentUuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function getSensitiveWords(
  params: SensitiveWordQuery,
  options?: ApiRequestOptions,
) {
  return request<SensitiveWordListResult>(SENSITIVE_WORD_PREFIX, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function createSensitiveWord(
  body: CreateSensitiveWordPayload,
  options?: ApiRequestOptions,
) {
  return request(SENSITIVE_WORD_PREFIX, {
    method: 'POST',
    data: body,
    ...(options || {}),
  });
}

export async function updateSensitiveWord(
  uuid: string,
  body: UpdateSensitiveWordPayload,
  options?: ApiRequestOptions,
) {
  return request(`${SENSITIVE_WORD_PREFIX}/${uuid}`, {
    method: 'PUT',
    data: body,
    ...(options || {}),
  });
}

export async function updateSensitiveWordStatus(
  uuid: string,
  body: UpdateSensitiveWordStatusPayload,
  options?: ApiRequestOptions,
) {
  return request(`${SENSITIVE_WORD_PREFIX}/${uuid}/status`, {
    method: 'PUT',
    data: body,
    ...(options || {}),
  });
}

export async function deleteSensitiveWord(
  uuid: string,
  options?: ApiRequestOptions,
) {
  return request(`${SENSITIVE_WORD_PREFIX}/${uuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}
